import {
  type IStore,
  SuprSendFeedProvider,
  useFeedClient,
  useFeedData,
} from "@suprsend/react";
import { useRouter } from "@tanstack/react-router";

import { Button } from "@/components/button";
import { useBulkUpdateAppNotifications } from "@/hooks/useAppNotifications";
import { useInHouseNotifications } from "@/hooks/useInHouseNotifications";
import { useWhitelabel } from "@/hooks/useWhitelabel";

import { FeaturePageLayout } from "../shared/layout";
import { InHouseNotificationFeed } from "./components/InHouseNotificationFeed";
import { NotificationFeed } from "./components/NotificationFeed";

const NOTIFICATION_STORES: IStore[] = [
  { label: "All", storeId: "All" },
  {
    label: "Requests",
    query: { tags: "request" },
    storeId: "request",
  },
  {
    label: "Mentions",
    query: { tags: "mentions" },
    storeId: "mentions",
  },
  {
    label: "Archived",
    query: { archived: true },
    storeId: "Archived",
  },
];

function HeaderActions() {
  const router = useRouter();
  const feedClient = useFeedClient();
  const feedData = useFeedData();

  const archiveAllNotifications = () => {
    for (const notification of feedData?.notifications ?? []) {
      if (!notification.archived) {
        feedClient?.markAsArchived(notification.n_id);
      }
    }
  };

  return (
    <div className="flex flex-row ml-auto">
      <Button
        className="mr-3"
        onPress={() => router.navigate({ to: "/notifications/settings" })}
        variant="secondary"
      >
        Settings
      </Button>
      <Button
        className="mr-3"
        onPress={archiveAllNotifications}
        variant="secondary"
      >
        Archive all
      </Button>
      <Button onPress={() => feedClient?.markAllAsRead()}>
        Mark all as read
      </Button>
    </div>
  );
}

function InHouseHeaderActions() {
  const router = useRouter();
  const { mutate: bulkUpdate } = useBulkUpdateAppNotifications();

  return (
    <div className="flex flex-row ml-auto">
      <Button
        className="mr-3"
        onPress={() => router.navigate({ to: "/notifications/settings" })}
        variant="secondary"
      >
        Settings
      </Button>
      <Button
        className="mr-3"
        onPress={() => bulkUpdate({ action: "archiveAll" })}
        variant="secondary"
      >
        Archive all
      </Button>
      <Button onPress={() => bulkUpdate({ action: "markAllRead" })}>
        Mark all as read
      </Button>
    </div>
  );
}

export default function Notifications() {
  const { config, isWhitelabel } = useWhitelabel();
  const useInHouse = useInHouseNotifications();

  if (useInHouse) {
    return (
      <div className="p-xl">
        <FeaturePageLayout
          headerContent={<InHouseHeaderActions />}
          title="Notifications"
        >
          <section className="bg-bg-primary flex p-5">
            <InHouseNotificationFeed />
          </section>
        </FeaturePageLayout>
      </div>
    );
  }

  return (
    <SuprSendFeedProvider
      stores={NOTIFICATION_STORES}
      tenantId={isWhitelabel && config ? config.tenantId! : "default"}
    >
      <div className="p-xl">
        <FeaturePageLayout
          headerContent={<HeaderActions />}
          title="Notifications"
        >
          <section className="bg-bg-primary flex p-5">
            <NotificationFeed />
          </section>
        </FeaturePageLayout>
      </div>
    </SuprSendFeedProvider>
  );
}
